import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Loader2, Trash2, Upload } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => (url?.startsWith("http") ? url : `${BASE}${url}`);
const LABELS = ["Front", "Rear", "Side", "Interior", "Other"];

export const FleetGalleryPanel = () => {
  const [vehicles, setVehicles] = useState([]);
  const [galleries, setGalleries] = useState({});
  const [vid, setVid] = useState("");
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState("Front");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/fleet/galleries").then((r) => setGalleries(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/vehicles").then((r) => { setVehicles(r.data); setVid(r.data[0]?.id || ""); }).catch(() => {});
    load();
  }, [load]);

  const images = galleries[vid] || [];

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !vid) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", label);
      fd.append("file", file);
      await api.post(`/admin/fleet-gallery/${vid}`, fd);
      toast.success("Image added to gallery");
      setFile(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (imageId) => {
    try {
      await api.delete(`/admin/fleet-gallery/${vid}/${imageId}`);
      toast.success("Image removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="fleet-gallery-panel">
      <div className="bg-white rounded-lg border border-[#e2ebe4] p-5">
        <h2 className="heading-upper text-sm tracking-widest text-[#144733] mb-1">Fleet gallery manager</h2>
        <p className="text-xs text-[#57685e] mb-4">Replace the generated placeholders with real vehicle photos — customers see changes instantly on the Fleet page.</p>
        <label className="field-label">Vehicle</label>
        <select className="field-input" value={vid} onChange={(e) => setVid(e.target.value)} data-testid="gallery-vehicle-select">
          {vehicles.map((v) => <option key={v.id} value={v.id}>Group {v.group} — {v.name}</option>)}
        </select>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4" data-testid="gallery-image-grid">
          {images.map((im) => (
            <div key={im.image_id} className="relative rounded-lg overflow-hidden border border-[#e2ebe4] group">
              <img src={imgSrc(im.url)} alt={im.label} className="w-full h-24 object-cover" loading="lazy" />
              <span className="absolute bottom-1 left-1 bg-[#0F1E17]/70 text-white text-[9px] px-1.5 py-0.5 rounded">{im.label}{im.source === "upload" ? " · uploaded" : ""}</span>
              <button type="button" onClick={() => remove(im.image_id)}
                className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                data-testid={`gallery-delete-${im.image_id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          {images.length === 0 && <p className="text-xs text-[#8a978f] col-span-full py-4">No images yet for this vehicle.</p>}
        </div>

        <form onSubmit={upload} className="mt-5 flex flex-wrap items-end gap-3 border-t border-dashed border-[#e2ebe4] pt-4">
          <div>
            <label className="field-label">Angle label</label>
            <select className="field-input" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="gallery-label-select">
              {LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Photo (JPG/PNG/WEBP, max 8 MB)</label>
            <input type="file" accept="image/jpeg,image/png,image/webp" className="field-input"
              onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="gallery-file-input" />
          </div>
          <button type="submit" disabled={!file || busy} className="btn-cta rounded px-5 py-2.5 text-xs flex items-center gap-2 disabled:opacity-50" data-testid="gallery-upload-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload image
          </button>
        </form>
      </div>
    </div>
  );
};
